import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /**
   * The instance that currently owns the open quick-add modal. Only this instance
   * re-renders the modal on an in-modal variant change — every card has its own
   * quick-add-component, but there is a single shared dialog.
   * @type {QuickAddComponent | null}
   */
  static #activeQuickAdd = null;

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /**
   * Which product the shared modal currently holds. The dialog is one element
   * reused for every card, so opening product B after product A morphs A's
   * nodes into B's: the reused <img> keeps showing A's already-decoded picture
   * until B's file arrives, and the shopper briefly sees the wrong product in
   * the modal (go-live QA 2026-09-03, Mathias: "it loads something else
   * first"). Wiping the stale content before the morph trades that for a
   * blank frame, which reads as loading instead of as the wrong item.
   * @type {string | null}
   */
  static #modalProductKey = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    // Capture phase: must snapshot the event's shared html document BEFORE the
    // dialog-level listeners (product-price, product-inventory, product-form) consume
    // it — morph MOVES nodes out of its source tree, so by bubble time the document
    // is gutted (empty price container, empty button label).
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleModalVariantUpdate, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleModalVariantUpdate, { capture: true });
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the modal body when the variant changes *inside* the modal, so the
   * price, media, availability and add/notify button reflect the selected variant.
   * The modal's variant picker lives outside `quick-add-component`, so the variant
   * HTML it fetches is not applied to the modal automatically — we do it here.
   * @param {Event} event - The variant update event.
   */
  #handleModalVariantUpdate = (event) => {
    // Only the component that opened the modal reacts, and only to changes that
    // originate inside the modal (not the card face or the PDP).
    if (QuickAddComponent.#activeQuickAdd !== this) return;
    if (!(event.target instanceof HTMLElement) || !event.target.closest('quick-add-dialog')) return;

    const html = /** @type {{ detail?: { data?: { html?: Document } } }} */ (event).detail?.data?.html;
    const gridElement = html?.querySelector('[data-product-grid-content]');
    if (!gridElement) return;

    const grid = /** @type {Element} */ (gridElement.cloneNode(true));

    // The document's variant-picker was already consumed by updateVariantPicker()
    // before this event was dispatched — graft a copy of the live (already-updated)
    // modal picker into the clone so the morph below doesn't wipe it.
    const modalContent = document.getElementById('quick-add-modal-content');
    const livePicker = modalContent?.querySelector('variant-picker');
    const gridPicker = grid.querySelector('variant-picker');
    if (livePicker && gridPicker) gridPicker.replaceWith(livePicker.cloneNode(true));

    // Same for the gallery: media-gallery.js swaps it itself once the new
    // variant's images are decoded (SPI-37). Morphing it here first would put
    // the new, still-unloaded slides on screen and collapse the column.
    const liveGallery = modalContent?.querySelector('media-gallery');
    const gridGallery = grid.querySelector('media-gallery');
    if (liveGallery && gridGallery) gridGallery.replaceWith(liveGallery.cloneNode(true));

    // Re-render from the selected variant's HTML, but do NOT re-sync from the card
    // (that would snap the selection back to the card's variant).
    this.updateQuickAddModal(grid, { syncFromCard: false });
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        if (QuickAddComponent.#activeQuickAdd === this) QuickAddComponent.#activeQuickAdd = null;
      },
      { once: true }
    );
  }

  /**
   * Closes an "added to cart" confirmation modal that is still open.
   *
   * The confirmation modal (snippets/curaprox-atc-modal.liquid) carries its own
   * "Often bought together" cards, each with a quick-add. Choosing options on one
   * of them used to open THIS dialog on top of the still-open confirmation — two
   * stacked modals — and once the add went through, the quick-add dialog closed
   * and left the shopper looking at a confirmation that still named the PREVIOUS
   * product. One modal at a time: close that one first, and let the add reopen a
   * fresh confirmation for the product actually added.
   *
   * Queried by tag rather than imported so this module keeps no dependency on
   * atc-modal.js (which is only loaded where the modal exists); `closeDialog` is
   * DialogComponent's own instance method on the upgraded element.
   *
   * @returns {Promise<void>}
   */
  async #closeOpenConfirmationModal() {
    const modal = /** @type {any} */ (
      [...document.querySelectorAll('curaprox-atc-modal')].find((element) => element.querySelector('dialog')?.open)
    );

    // Awaited so the closing modal finishes restoring the body scroll lock before
    // showDialog() takes it over — otherwise its cleanup lands a beat later and
    // unlocks the page underneath the dialog we just opened.
    if (typeof modal?.closeDialog === 'function') await modal.closeDialog();
  }

  #openQuickAddModal = async () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    await this.#closeOpenConfirmationModal();

    QuickAddComponent.#activeQuickAdd = this;
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid, { syncFromCard = true } = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Different product than the modal last showed → start from an empty
    // container so no node (and no decoded image) of the previous product
    // survives the morph. In-modal variant changes keep the same key and still
    // get the cheap in-place morph.
    const productKey = this.closest('product-card')?.dataset.productId || this.productPageUrl;
    if (QuickAddComponent.#modalProductKey !== productKey) {
      modalContent.replaceChildren();
      QuickAddComponent.#modalProductKey = productKey;
    }

    morph(modalContent, productGrid);

    if (syncFromCard) this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Click outside = close, matching the add-to-cart modal (the desktop card has no
    // visible X; the outside area carries the blue-circle/white-X cursor instead — see
    // snippets/quick-add-modal-styles.liquid). DialogComponent's own outside-click
    // handler does not fire for this dialog, so bind it here: a click on the ::backdrop
    // is dispatched with the <dialog> ELEMENT ITSELF as target, while every click on
    // the card resolves to a descendant, so the identity check is exact (no
    // bounding-box maths, no false positives from clicks on the card's own padding).
    this.addEventListener('click', this.#handleBackdropClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog when the backdrop (the area outside the card) is clicked.
   * @param {MouseEvent} event - The click event
   */
  #handleBackdropClick = (event) => {
    if (event.target !== this.refs.dialog) return;

    this.closeDialog();
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
